import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './BulkUploadPrice.css';

const BulkUploadPrice = () => {
  const { t, i18n } = useTranslation();
  const today = new Date();

  const MONTH_NAMES = [0,1,2,3,4,5,6,7,8,9,10,11].map(m =>
    new Date(2000, m, 1).toLocaleString(i18n.language === 'en' ? 'en-US' : 'es-EC', { month: 'long' })
  );
  const DAY_NAMES = [0,1,2,3,4,5,6].map(d =>
    new Date(2000, 0, d + 2).toLocaleString(i18n.language === 'en' ? 'en-US' : 'es-EC', { weekday: 'short' })
  );
  const [year, setYear]     = useState(today.getFullYear());
  const [month, setMonth]   = useState(today.getMonth()); // 0-indexed
  const [zone, setZone]     = useState('norte');
  const [prices, setPrices] = useState({});               // { 'YYYY-MM-DD': '7.50' }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  // Generar todos los días del mes seleccionado
  const daysInMonth = useMemo(() => {
    const days = [];
    const count = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= count; d++) {
      const date = new Date(year, month, d);
      const key  = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ date, key, day: d, weekday: DAY_NAMES[date.getDay()] });
    }
    return days;
  }, [year, month]);

  const handlePriceChange = (key, value) => {
    setPrices(prev => ({ ...prev, [key]: value }));
  };

  // Rellenar toda la columna con el mismo precio (útil si el precio no cambió)
  const fillAll = (value) => {
    const filled = {};
    daysInMonth.forEach(({ key }) => {
      filled[key] = value;
    });
    setPrices(filled);
  };

  const handleSubmit = async () => {
    const payload = daysInMonth
      .filter(({ key }) => prices[key] && prices[key].trim() !== '')
      .map(({ key }) => ({
        date: `${key}T12:00:00`,
        price_per_box: parseFloat(prices[key]),
        zone,
      }));

    if (payload.length === 0) {
      setError(t('bulk_error_empty'));
      return;
    }

    setSubmitting(true);
    setResult(null);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/prices/bulk-upload/`,
        { prices: payload },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setResult(res.data);
      // Limpiar precios cargados exitosamente
      if (res.data.errors?.length === 0) setPrices({});
    } catch (err) {
      setError(err.response?.data?.error || t('upload_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const filledCount = daysInMonth.filter(({ key }) => prices[key] && prices[key].trim() !== '').length;

  return (
    <div className="bulk-container">
      <h2>{t('bulk_title')}</h2>
      <p className="bulk-subtitle">{t('bulk_subtitle')}</p>

      {/* Controles de zona/mes/año */}
      <div className="bulk-controls">
        <label>
          {t('zone')}
          <select value={zone} onChange={e => setZone(e.target.value)}>
            <option value="norte">{t('zona_norte')}</option>
            <option value="sur">{t('zona_sur')}</option>
          </select>
        </label>
        <label>
          {t('bulk_month')}
          <select value={month} onChange={e => { setMonth(Number(e.target.value)); setPrices({}); setResult(null); }}>
            {MONTH_NAMES.map((n, i) => (
              <option key={i} value={i}>{n}</option>
            ))}
          </select>
        </label>
        <label>
          {t('bulk_year')}
          <select value={year} onChange={e => { setYear(Number(e.target.value)); setPrices({}); setResult(null); }}>
            {[today.getFullYear() - 2, today.getFullYear() - 1, today.getFullYear()].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>

        <div className="bulk-fill-row">
          <span className="bulk-fill-label">{t('bulk_fill_with')}</span>
          <input
            type="number"
            step="0.01"
            placeholder="Ej: 7.50"
            className="bulk-fill-input"
            onKeyDown={e => { if (e.key === 'Enter') fillAll(e.target.value); }}
            onBlur={e => { if (e.target.value) fillAll(e.target.value); }}
          />
          <span className="bulk-fill-hint">{t('bulk_fill_hint')}</span>
        </div>
      </div>

      {/* Tabla de días */}
      <div className="bulk-table-wrapper">
        <table className="bulk-table">
          <thead>
            <tr>
              <th>{t('bulk_col_day')}</th>
              <th>{t('bulk_col_date')}</th>
              <th>{t('bulk_col_price')}</th>
            </tr>
          </thead>
          <tbody>
            {daysInMonth.map(({ key, day, weekday }) => {
              const isWeekend = weekday === 'Dom' || weekday === 'Sáb';
              return (
                <tr key={key} className={isWeekend ? 'bulk-row--weekend' : ''}>
                  <td className="bulk-weekday">{weekday}</td>
                  <td className="bulk-date">
                    {String(day).padStart(2, '0')} {MONTH_NAMES[month].slice(0, 3)} {year}
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="—"
                      value={prices[key] || ''}
                      onChange={e => handlePriceChange(key, e.target.value)}
                      className="bulk-price-input"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer de acción */}
      <div className="bulk-footer">
        <span className="bulk-count">
          {t('bulk_days_filled', { filled: filledCount, total: daysInMonth.length })}
        </span>
        <button
          className="bulk-submit-btn"
          onClick={handleSubmit}
          disabled={submitting || filledCount === 0}
        >
          {submitting ? t('bulk_submitting') : t('bulk_submit_btn', { count: filledCount })}
        </button>
      </div>

      {/* Resultado */}
      {result && (
        <div className="bulk-result">
          <strong>{t('bulk_load_complete')}</strong>
          <span>{result.created} creados · {result.updated} actualizados · {result.total_processed} total</span>
          {result.errors?.length > 0 && (
            <span className="bulk-result-errors">{t('bulk_with_errors', { count: result.errors.length })}</span>
          )}
        </div>
      )}

      {error && <div className="bulk-error">{error}</div>}

      <p className="bulk-note">{t('bulk_note')}</p>
    </div>
  );
};

export default BulkUploadPrice;
